import axios from "axios";
import store from "@/store";
import router from "@/router";
import { saveAfterLoginPath } from "@/utils/deeplink";
import { initUserData } from "@/util";

/**
 * 인증이 끊겨 로그인 화면으로 보낼 때, 보고 있던 화면을 기억해 두었다가 로그인 후 복귀시킨다.
 *
 * 문자 딥링크로 갱신 화면에 들어왔는데 토큰 리프레시까지 실패한 경우가 대표적이다.
 * 이게 없으면 재로그인 후 홈으로 떨어져서, 사용자는 문자를 다시 찾아 눌러야 한다.
 *
 * 보호가 필요한 화면(requiresAuth)만 기억한다. 아무 화면이나 복원하면
 * 로그인 후 엉뚱한 곳으로 가는 것처럼 보인다.
 *
 * 메인(/)은 비로그인으로도 보는 화면이라 로그인 화면으로 보내지 않는다. 로그인 정보만 지우고
 * 비로그인 메인으로 둔다. 리프레시 토큰이 무효가 된 상태(다른 기기 로그인 등)로 앱을 켜면
 * 메인에서 바로 로그인 화면으로 튕기던 문제가 있었다.
 */
const goLoginKeepingPath = async () => {
    const current = router.currentRoute.value;
    if (!current || current.path === "/") {
        await initUserData();
        return;
    }
    if (current.meta && current.meta.requiresAuth) {
        saveAfterLoginPath(current.fullPath);
    }
    await router.push("/login");
};

// ngrok 무료 플랜은 브라우저 요청에 경고 페이지(ERR_NGROK_6024)를 끼워 넣는다.
// 그 응답은 200 + text/html 이면서 Access-Control-Allow-Origin 이 없어 CORS 에러로 나타난다.
// 이 헤더가 있으면 경고 페이지를 건너뛴다. API 주소가 ngrok 일 때만 붙이므로 운영에는 영향이 없다.
const isNgrokApi = (process.env.VUE_APP_API_URL || "").includes("ngrok")

const axiosInstance = axios.create({
    baseURL: process.env.VUE_APP_API_URL,
    headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "*",
        "Access-Control-Allow-Headers":
            "Origin, X-Requested-With, Content-Type, Accept, Authorization",
        ...(isNgrokApi ? {"ngrok-skip-browser-warning": "true"} : {}),
    },
});

axiosInstance.interceptors.request.use(
    function (config) {
        const accessToken = store.getters.getAccessToken;
        if (accessToken) config.headers["Authorization"] = `Bearer ${accessToken}`;
        return config;
    },
    function (error) {
        return Promise.reject(error);
    }
);
let isTokenRefreshing = false;
let refreshSubscribers = [];

const onTokenRefreshed = () => {
    refreshSubscribers.forEach((s) => {
        try {
            s.resolve();
        } catch (err) {
            if (process.env.NODE_ENV !== "production") {
                // 구독자 콜백 실행 중 오류 로깅
                // eslint-disable-next-line no-console
                console.warn("onTokenRefreshed resolve error:", err);
            }
        }
    });
    refreshSubscribers = [];
};

const onTokenRefreshFailed = (err) => {
    refreshSubscribers.forEach((s) => {
        try {
            s.reject(err);
        } catch (e) {
            if (process.env.NODE_ENV !== "production") {
                // 구독자 콜백 실행 중 오류 로깅
                // eslint-disable-next-line no-console
                console.warn("onTokenRefreshFailed reject error:", e);
            }
        }
    });
    refreshSubscribers = [];
};

const addRefreshSubscriber = (subscriber) => {
    // subscriber: { resolve, reject }
    refreshSubscribers.push(subscriber);
};
// const flushSubscriber = () => {
//     refreshSubscribers = [];
// };

axiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        if (window.location.pathname === "/" && !store.getters.getAccessToken) {
            return false
        }
        const {
            config: originalRequest,
            response: {status},
        } = error;
        if (status === 403) {
            await goLoginKeepingPath();
        } else if (status === 401) {
          // 대기 중 원요청을 Promise로 등록(성공 시 재시도, 실패 시 에러 전파)
          let retryOriginalRequest = new Promise((resolve, reject) => {
            addRefreshSubscriber({
              resolve: () => resolve(axiosInstance(originalRequest)),
              reject,
            });
          });


          if (!isTokenRefreshing) {
            isTokenRefreshing = true;
            try {
              const accessToken = await store.getters.getAccessToken;
              const refreshToken = await store.getters.getRefreshToken;
              const resp = await axiosInstance.post(
                  `/members/token/refresh`,
                  { refreshToken, accessToken },
                  { validateStatus: () => true } // 2xx가 아니어도 reject하지 않음
              );

              // 비정상 상태(401 등)는 실패로 간주하여 대기중 요청을 reject
              if (resp.status < 200 || resp.status >= 300) {
                await store.commit("setAccessToken", {accessToken: null});
                await store.commit("setRefreshToken", {refreshToken: null});
                onTokenRefreshFailed(new Error(`Token refresh failed (${resp.status})`));
                await goLoginKeepingPath();
              } else {
                // 성공 시 토큰 갱신 후 대기 중 요청들을 깨워 재시도
                const { accessToken: newAccessToken } = resp.data;
                await store.commit("setAccessToken", { accessToken: newAccessToken });
                onTokenRefreshed();
              }
            } catch (e) {
              // 네트워크 오류 등 예외도 실패 처리
              await store.commit("setAccessToken", {accessToken: null});
              await store.commit("setRefreshToken", {refreshToken: null});
              onTokenRefreshFailed(e);
              await goLoginKeepingPath();
            } finally {
              isTokenRefreshing = false;
            }
          }

          return retryOriginalRequest;
          // console.log("@@@3");
          //   let retryOriginalRequest = new Promise((resolve) => {
          //       addRefreshSubscriber(() => {
          //           resolve(axiosInstance(originalRequest));
          //       });
          //   });
          // console.log("@@@5");
          // console.log("retryOriginalRequest", retryOriginalRequest);
          // console.log("isTokenRefreshing", isTokenRefreshing);
          //   if (!isTokenRefreshing) {
          //     console.log("@@@6");
          //       isTokenRefreshing = true;
          //       try {
          //         console.log("@@@7");
          //           const accessToken = await store.getters.getAccessToken;
          //           const refreshToken = await store.getters.getRefreshToken;
          //         console.log("@@@9");
          //           let {
          //               data: {accessToken: newAccessToken},
          //           } = await axiosInstance.post(
          //               `/members/token/refresh`,
          //               {
          //                   refreshToken: refreshToken,
          //                   accessToken: accessToken,
          //               }
          //           );
          //         console.log("@@@10");
          //           console.log("newAccessToken", newAccessToken);
          //           await store.commit("setAccessToken", {accessToken: newAccessToken});
          //       } catch (e) {
          //         console.log("@@@8");
          //           await store.commit("setAccessToken", {accessToken: null});
          //           await store.commit("setRefreshToken", {refreshToken: null});
          //           flushSubscriber();
          //           await router.push("/login");
          //       } finally {
          //         console.log("@@@11");
          //       }
          //       isTokenRefreshing = false;
          //       onTokenRefreshed();
          //   }
          //   return retryOriginalRequest;
        }else if(status === 412) {
            await store.commit("resetMember");
            await store.commit("resetAuth");
            await store.commit("resetCouponStates");
            await store.commit("setMenuClose");
            alert("중복 로그인으로 인해 로그아웃 됩니다.")
            await router.push("/");
        }
        return Promise.reject(error);
    }
);

export default axiosInstance;